import React, { useEffect } from "react";
import StepIndicator from "../subComponent/step-indicator/StepIndicator";
import { SuccessAlertComponent } from "../odsCalloutBox/SuccessAlertComponent";
import { FormSectionElements, ProgramContainer } from "../../entities/formTemplateDataType";
import { EmailButtonComponent } from "../subComponent/EmailButtonComponent";
import { Heading5 } from "../../theme";
import { WarningAlertComponent } from "../odsCalloutBox/WarningAlertComponent";
import FeedbackComponent from "../subComponent/FeedbackComponent";
import { FeedbackStatus } from "../../entities/Constants";
import TrackPageView from "../../utilities/TrackPageView";
import createAccount from "../../../public/assets/images/create-account.png"
import renewalReminders from "../../../public/assets/images/renewal-reminders.png"
import moreServices from "../../../public/assets/images/more-services.png"
import { styled } from "styled-components";
import DataUtils from "../../utilities/DataUtils";
import axios from "axios";
import { processPayload } from "../../utilities/custom/ODRUtils";
import { getUserProfileFromSession } from "../../services/CxpProfileService";
import { normalizeUrl } from "../../utilities/URLUtils";
import { getEnvVar } from "../../config/RuntimeConfig";

interface Props {
    programContainer: ProgramContainer
    isEng: boolean;
    transactionId: string;
    Visible: boolean;
    callback: (p: any) => void,
    uploadReceiptSuccess: boolean
}

const Divider = styled.div`
    width: 100%;
    height: 0;
    border-top: 3px solid #333;
    opacity: 1;
    transform: rotate(0deg);
    margin: 50px auto;
`;

const ReRegisterLink = styled.a`
    font-family: 'Open Sans', serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 25.6px;
    vertical-align: middle;
    color: #0066cc;
`;

const CONTENT = {
    en: {
        emailTitle: "Send transaction receipt to this email address:",
        emailDescription: "Your email address will only be used to send you a transaction receipt and will not be retained.",

        backToHome: "Back to home",
        backToOntario: "Back to Ontario.ca",

        pageTitle: "order-confirmation",
        saveReceiptTitle: "Save your confirmation receipt",

        orderIdLabel: "Order ID: ",

        donorConsentAlert: "You have updated your organ and tissue donor consent.",
        donorConsentAlert1: "You are a registered organ and tissue donor.",
        donorConsentAlert2: "You are no longer registered as an organ and tissue donor.",
        confirmationReceiptBody: "A confirmation receipt will be sent to your inbox.",
        confirmationReceiptBodyForUser: "An email confirmation with your receipt was sent to {{email}}",

        technicalIssueWarning: "Due to a technical issue, we were unable to generate your downloadable receipt. Please keep a record of your order ID and contact ServiceOntario if you do not receive the health card in 4-6 weeks.",

        downloadPdfButton: "Download PDF confirmation",

        importantNoticeGuest: "IMPORTANT: Check your downloaded files before closing this screen. You will not be able to access your confirmation here again.",
        importantNoticeUser: "Note: Check your downloaded files before closing this screen.",

        registrationThankYou: "Thank you for registering to save lives through organ and tissue donation. Take this opportunity to talk to your family and friends. Sharing your decision ensures your wishes can be honoured.",
        keepRecordInstructions: "To keep a record of your registered donation decision, you can download a copy of this confirmation to your device and send a copy to your email below.",

        reRegisterAgain: "Want to register as a donor again?",
        reRegisterContent1: "If you change your mind later, you can",
        reRegisterContent2: "register as an organ and tissue donor",
        reRegisterContent3: "again.",
        reRegisterLink: "https://www.ontario.ca/page/organ-and-tissue-donor-registration"
    },

    fr: {

        emailTitle: "Envoi du reçu de transaction à cette adresse de courriel :",
        emailDescription: "Votre adresse de courriel ne servira qu’à vous envoyer le reçu de transaction et ne sera pas conservée.",

        backToHome: "Retour à l'accueil",
        backToOntario: "Revenir à Ontario.ca",

        pageTitle: "order-confirmation",
        saveReceiptTitle: "Conservez votre reçu de confirmation.",

        orderIdLabel: "ID de la commande:",

        donorConsentAlert: "Vous avez mis à jour votre consentement au don d’organes et de tissus humains.",
        donorConsentAlert1: "Votre inscription au don d’organes et de tissus humains est confirmée.",
        donorConsentAlert2: "Vous n’êtes plus inscrite ou inscrit au don d’organes et de tissus humains.",
        confirmationReceiptBody: "Un message de confirmation sera envoyé à votre boîte de réception.",
        confirmationReceiptBodyForUser: "Un courriel de confirmation avec votre reçu a été envoyé à {{email}}",

        technicalIssueWarning: "En raison d'un problème technique, nous n'avons pas pu générer votre reçu téléchargeable. Veuillez conserver une trace de votre identifiant de transaction et contacter ServiceOntario si vous ne recevez pas la carte Santé dans un délai de 4 à 6 semaines.",

        downloadPdfButton: "Télécharger la confirmation au format PDF",

        importantNoticeGuest: "IMPORTANT : Vérifiez vos fichiers téléchargés avant de fermer cet écran. Vous ne pourrez plus accéder à votre confirmation ici.",
        importantNoticeUser: "Remarque : Vérifiez vos fichiers téléchargés avant de fermer cet écran.",

        registrationThankYou: "Merci pour votre inscription au don d’organes et de tissus humains. Vous sauverez des vies. Profitez de cette occasion pour en parler à votre famille et à vos amis et amies. Faire connaître votre décision permet de vous assurer que vos souhaits seront respectés.",
        keepRecordInstructions: "Pour conserver une preuve de votre décision en matière de don, vous pouvez télécharger une copie de la présente confirmation sur votre appareil et en envoyer une copie à votre adresse de courriel ci-dessous.",

        reRegisterAgain: "Vous souhaitez vous réinscrire au don?",
        reRegisterContent1: "Si vous changez d'avis, vous pouvez",
        reRegisterContent2: "vous réinscrire au don d'organes",
        reRegisterContent3: "et de tissus humains.",
        reRegisterLink: "https://www.ontario.ca/page/organ-and-tissue-donor-registration"
    }
};

const getContent = (isEng: boolean) => {
    return CONTENT[isEng ? 'en' : 'fr'];
};

export class ODROrderConfirmationComponent extends React.Component<Props> {

    field: FormSectionElements;

    constructor(props: Props) {
        super(props);

        this.field = new FormSectionElements();
        this.field.ENUTitle = "Send transaction receipt to this email address:";
        this.field.FRATitle = "Envoi du reçu de transaction à cette adresse de courriel:";

        this.field.ENUDescription = "Your email address will only be used to send you a transaction receipt and will not be retained.";
        this.field.FRADescription = "Votre adresse de courriel ne servira qu’à vous envoyer le reçu de transaction et ne sera pas conservée.";
    }

    sendEmail = (field: any, token: string) => {

        const language = this.props?.isEng ? "EN" : "FR";

        const emailUrl = this.props.programContainer.APIBasePath + this.props.programContainer.emailAPI;

        const profile = getUserProfileFromSession();
        const data = DataUtils.collectFormValue(this.props.programContainer);

        const payload = processPayload(data);
        const emailPayload = {
            ...payload,
            transactionId: data?.verifyApiData?.transactionId || '',
            externalTransactionID: data?.verifyApiData?.externalTransactionID || '',
            receiver: field?.Value || profile?.email || '',
            donorName: data?.donorName || '',
            publicSecureIdentifier: profile?.userId || null,
            token: token || null,

            language: language,
            emailFor: data?.journeyType || '',
        };

        axios.post(emailUrl, emailPayload)
            .then(() => {

            })
            .catch(ex => {
                console.log(ex);
            })
    }

    downloadDocument() {
        const language = this.props?.isEng ? "EN" : "FR";
        const downloadUrl = this.props.programContainer.APIBasePath + this.props.programContainer.downloadAPI;

        const profile = getUserProfileFromSession();
        const data = DataUtils.collectFormValue(this.props.programContainer);

        const payload = processPayload(data);
        const downloadPayload = {
            ...payload,
            donorName: data?.donorName || '',
            pdfType: data?.journeyType || '',
            language: language,

            transactionId: data?.verifyApiData?.transactionId || '',
            externalTransactionID: data?.verifyApiData?.externalTransactionID || '',
            publicSecureIdentifier: profile?.userId || null,
        };


        fetch(downloadUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(downloadPayload)
        }).then(response => response.json())
            .then(data => {

                const binaryString = atob(data.pdfString);
                const bytes = Uint8Array.from(binaryString, char => char?.charCodeAt(0));
                const blob = new Blob([bytes], { type: "application/pdf" });
                const anchor = document.createElement("a");

                anchor.href = window.URL.createObjectURL(blob);
                anchor.download = `Receipt-${this.props.transactionId}`;
                anchor.hidden = true;

                document.body.append(anchor);
                anchor.click();
                anchor.remove();

                window.addEventListener(
                    "focus",
                    () => {
                        URL.revokeObjectURL(anchor.href);
                    },
                    {
                        once: true,
                    }
                )
            });
    }

    componentDidMount() {

        window.scrollTo(0, 0);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<{}>, snapshot?: any): void {
        if (!this.props.Visible) return;
        const title = "your-renewal-application-has-been-submitted"
        const isEng = window.location.href.indexOf("/fr/") == -1;

        let preUrl = window.location.origin + "/on-form/odr/review-your-information";
        if (!prevProps.isEng) {
            preUrl = window.location.origin + "/on-form/fr/odr/review-your-information";
        }

        sessionStorage.setItem("previousUrl", preUrl);

        let urlString = window.location.origin + "/on-form/odr/" + title;

        if (!isEng) {
            urlString = window.location.origin + "/on-form/fr/odr/" + title;
        }
        window.history.pushState({}, "", normalizeUrl(urlString));

        if (this.props.Visible && sessionStorage.getItem("languageChange") != "true" && sessionStorage.getItem("ODRSummaryFired") != "true") {
            TrackPageView(this.props.programContainer);
            sessionStorage.setItem("ODRSummaryFired", "true");
            sessionStorage.removeItem("languageChange")
        }
    }

    componentWillUnmount(): void {
        sessionStorage.removeItem("ODRSummaryFired");
        sessionStorage.removeItem("languageChange");    
    }

    render() {
        if (!this.props?.Visible) return null;

        const content = getContent(this.props.isEng);
        const data = DataUtils.collectFormValue(this.props.programContainer);

        const surveyUrlParams = {
            svc: data?.journeyType === 'withdraw'
                ? 'withdraw' : data?.journeyType === 'registration'
                    ? 'register' : data?.journeyType === 'update' ? 'change' : null,
            src: 'link',
            sglocale: this.props.isEng ? 'en' : 'fr-ca',
        }



        const profile = getUserProfileFromSession();
        let backToLabel;
        let backToUrl;
        let body;
        if (profile?.email) {
            backToLabel = content.backToHome;
            backToUrl = getEnvVar('PUBLIC_CXP_WEB_BASE_PATH') +
                "/auth/signin?redirectUri=" +
                (this.props?.isEng ? "" : "/fr") +
                "/dashboard";
            body = content.confirmationReceiptBodyForUser.replace("{{email}}", profile.email);
        } else {
            backToLabel = content.backToOntario;
            backToUrl = this.props?.isEng
                ? "https://www.ontario.ca/page/organ-and-tissue-donor-registration"
                : "https://www.ontario.ca/fr/page/inscription-au-don-dorganes-et-de-tissus-humains";

        }

        const alertTitle = data?.journeyType === 'withdraw'
            ? content.donorConsentAlert2 : data?.journeyType === 'registration'
                ? content.donorConsentAlert1 : data?.journeyType === 'update'
                    ? content.donorConsentAlert : '';
        const isReceiptUploaded: boolean = this.props?.uploadReceiptSuccess;

        return (
            <div>
                <div>
                    <StepIndicator
                        withbackbtn={true}
                        sectionSequence={-1}
                        title={backToLabel}
                        backToUrl={backToUrl}
                        programContainer={this.props?.programContainer}
                        isEng={this.props?.isEng}
                        callback={(currentStep) => {
                            this.setState({ stepperIndex: currentStep })
                        }}>
                    </StepIndicator>
                </div>

                <div>
                    {isReceiptUploaded ?
                        <SuccessAlertComponent
                            field={this.field}
                            title={alertTitle}
                            bodyMsg={body}
                            isEng={this.props.isEng} Visible={isReceiptUploaded} />
                        :
                        <WarningAlertComponent
                            title={alertTitle}
                            bodyMsg={content.technicalIssueWarning}
                            isEng={this.props.isEng}
                            Visible={!isReceiptUploaded} />
                    }

                </div>

                <div>
                    <h1>{content.saveReceiptTitle}</h1>
                    <p>{content.orderIdLabel}{this.props.transactionId}</p>
                    {data?.journeyType !== 'withdraw'
                        && (
                            <p>{content.registrationThankYou}
                            </p>
                        )
                    }
                    <p>{content.keepRecordInstructions}</p>
                </div>

                <div>
                    <button
                        className="ontario-button ontario-button--primary confirmation-button"
                        disabled={!isReceiptUploaded}
                        onClick={() => this.downloadDocument()}>
                        {content.downloadPdfButton}
                    </button>
                </div>

                <div>
                    <div style={{
                        display: profile && profile?.userId ? "none" : "block",
                        ...(!isReceiptUploaded && {
                            pointerEvents: 'none',
                            opacity: 0.5
                        })
                    }}>
                        <EmailButtonComponent
                            key={"EmailComponent"}
                            field={this.field}
                            editable={true}
                            disabled={!isReceiptUploaded}
                            title={this.props.isEng ?
                                <Heading5>{this.field.ENUTitle}</Heading5> :
                                <Heading5>{this.field.FRATitle}</Heading5>}
                            labelControlArray={null}
                            requiredField={false}
                            isEng={this.props.isEng}
                            enableRecaptcha={true}
                            callback={(field: any, token: string) => this.sendEmail(field, token)}>
                        </EmailButtonComponent>
                    </div>

                    <div>
                        <FeedbackComponent
                            programContainer={this.props.programContainer}
                            isEng={this.props.isEng}
                            transactionId={this.props.transactionId}
                            status={FeedbackStatus.Success}
                            isHyperlink={true}
                            params={surveyUrlParams}
                        />
                    </div>

                    {data?.journeyType === 'withdraw'
                        && (
                            <section>
                                <h3>
                                    {content.reRegisterAgain}
                                </h3>
                                <p>
                                    {content.reRegisterContent1} {' '}
                                    <ReRegisterLink
                                        href={content.reRegisterLink}
                                        className="text-blue-600 hover:text-blue-800 underline"
                                        target={"_blank"}
                                        rel="noopener noreferrer">
                                        {content.reRegisterContent2}{' '}
                                    </ReRegisterLink>
                                    <p>
                                        {content.reRegisterContent3}
                                    </p>
                                </p>

                            </section>
                        )
                    }

                    <Divider />

                    <section>
                        <AvailableServicesComponent isEng={this.props.isEng} />
                    </section>
                </div>
            </div>
        )
    }
}

const CardsContainer = styled.div`
    display: flex;
    gap: 24px;
    align-items: flex-start;
    flex-direction: column;

    @media (min-width: 768px) {
        flex-direction: row;
    }
`;

const Card = styled.div`
    width: 357.33px;
    height: 342px;
    border-radius: 4px;
    background-color: white;
    box-shadow: 0px 3px 8px 1px #00000066;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const ImageContainer = styled.div`
    flex: 1;
    overflow: hidden;
`;

const Image = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const Content = styled.div`
    padding: 24px;
    min-height: 140px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    font-family: 'Raleway', sans-serif;
`;

const Link = styled.a`
    font-family: Raleway, "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-weight: 700;
    font-size: 19px;
    line-height: 29px;
    letter-spacing: 0.4px;
    vertical-align: middle;
    text-decoration: underline;
    color: #0066CC;
    margin-bottom: 8px;
`;

const Description = styled.p`
    font-weight: 400;
    font-size: 16px;
    color: #1A1A1A;
    line-height: 1.5;
    margin: 0;
`;

const CARDS_CONTENT = {
    en: [
        {
            id: 1,
            title: "Create an account",
            description: "You're a few clicks away from creating an account. Create an account to complete services faster in the future.",
            image: createAccount
        },
        {
            id: 2,
            title: "Renewal reminders",
            description: "Get reminders before your licence plate, driver's licence, health card, or Ontario photo card expires.",
            image: renewalReminders
        },
        {
            id: 3,
            title: "Explore more services",
            description: "Check out other services you can complete easily from the comfort of your own home.",
            image: moreServices
        }
    ],
    fr: [
        {
            id: 1,
            title: "Créer un compte",
            description: "Vous n'êtes qu'à quelques clics de la création d'un compte. Créez un compte pour effectuer vos démarches plus rapidement à l'avenir.",
            image: createAccount
        },
        {
            id: 2,
            title: "Rappels de renouvellement",
            description: "Recevez des rappels avant l'expiration de votre plaque d'immatriculation, permis de conduire, carte Santé ou carte-photo de l'Ontario.",
            image: renewalReminders
        },
        {
            id: 3,
            title: "Explorer plus de services",
            description: "Découvrez d'autres services que vous pouvez effectuer facilement dans le confort de votre foyer.",
            image: moreServices
        }
    ]
};


const AvailableServicesComponent: React.FC<{ isEng: boolean }> = ({ isEng }) => {
    const cards = CARDS_CONTENT[isEng ? 'en' : 'fr'];
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    return (
        <CardsContainer>
            {cards.map((card) => (
                <Card
                    key={card.id}>

                    <ImageContainer>
                        <Image
                            src={card.image}
                            alt={card.title}
                        />
                    </ImageContainer>

                    <Content>
                        <Link
                            href="https://www.ontario.ca/page/government-ontario"
                            target={"_blank"}>
                            {card.title}
                        </Link>
                        <Description>
                            {card.description}
                        </Description>
                    </Content>
                </Card>
            ))}
        </CardsContainer>
    );
}
    ;